import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Container, Box, Typography, Button, Grid, useTheme } from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import GoogleReviewsWidget from "../components/GoogleReviewsWidget";
import BookingReviewsWidget from "../components/BookingReviewsWidget";
import Reveal from "../components/Reveal";
import { PageHero, Section, SectionTitle, ZoomImage } from "../components/layout";
import CONTACT_INFO from "../constants/contactInfo";

const HERO_IMAGE =
  "https://ik.imagekit.io/cabanasdelcampo/pileta/20250113_115540.jpg?updatedAt=1737417503079";
const TESTIMONIO_IMAGE =
  "https://ik.imagekit.io/cabanasdelcampo/exteriores/WhatsApp%20Image%202026-06-17%20at%2011.38.28%20(1).jpeg?updatedAt=1781707373615";

const TESTIMONIO = {
  autores: "Oscar y Analía",
  destacado:
    "Esto es un paraíso que todos deben conocer, siempre que se respete la serenidad y cadencia que el lugar amerita.",
  parrafos: [
    "En primer lugar gracias Silvina por todo, increíble predisposición para que estemos muy cómodos y muy bien atendidos.",
    "Y el lugar, bañado con la pristina frescura de los bosques serranos, armados con los aromáticos olores a yayos y hierbas serranas que caen de los cerros, el serpenteo del camino que lleva al Calamuchita, la frescura y sonora onda que ofrece el arroyo que pasa pegado a mi cuarto... ¡qué locura!",
    "Gracias Silvina por tu increíble hospitalidad. Esta es nuestra primera vez pero no la última.",
  ],
};

const NuestrosHuspedes = () => {
  const theme = useTheme();

  return (
    <Box>
      <PageHero image={HERO_IMAGE} overline="Nuestros huéspedes" title="Lo que dicen quienes nos visitan">
        Cada estadía deja una historia. Estas son algunas de las que nos compartieron.
      </PageHero>

      {/* Testimonio destacado */}
      <Section>
        <Container>
          <Grid container spacing={{ xs: 5, md: 10 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Reveal>
                <FormatQuoteIcon
                  sx={{ fontSize: 64, color: "primary.main", transform: "scaleX(-1)", mb: 1, ml: -1 }}
                />
                <Typography
                  component="blockquote"
                  sx={{
                    m: 0,
                    fontFamily: theme.typography.h1.fontFamily,
                    fontStyle: "italic",
                    fontWeight: 400,
                    fontSize: { xs: "1.6rem", md: "2.3rem" },
                    lineHeight: 1.25,
                    mb: 4,
                  }}
                >
                  {TESTIMONIO.destacado}
                </Typography>
                {TESTIMONIO.parrafos.map((p) => (
                  <Typography key={p} variant="body1" paragraph sx={{ color: "text.secondary" }}>
                    {p}
                  </Typography>
                ))}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
                  <Box sx={{ width: 40, height: 2, backgroundColor: "primary.main" }} />
                  <Typography sx={{ fontWeight: 600 }}>{TESTIMONIO.autores}</Typography>
                </Box>
              </Reveal>
            </Grid>
            <Grid item xs={12} md={5} sx={{ width: "100%" }}>
              <Reveal delay={150}>
                <ZoomImage sx={{ height: { xs: 300, md: 520 }, "& img": { objectPosition: "center 60%" } }}>
                  <img src={TESTIMONIO_IMAGE} alt="Atardecer en el parque de las cabañas" loading="lazy" />
                </ZoomImage>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Reseñas de Booking */}
      <Section sx={{ backgroundColor: theme.palette.sand.light, py: { xs: 8, md: 10 } }}>
        <Container>
          <Reveal>
            <SectionTitle overline="Booking.com" title="Reseñas en Booking">
              Opiniones verificadas de huéspedes que reservaron a través de Booking.
            </SectionTitle>
          </Reveal>
          <BookingReviewsWidget />
        </Container>
      </Section>

      {/* Reseñas de Google */}
      <Section sx={{ py: { xs: 8, md: 10 } }}>
        <Container>
          <Reveal>
            <SectionTitle overline="Google" title="Reseñas en Google">
              Lo que cuentan nuestros huéspedes en Google Maps.
            </SectionTitle>
          </Reveal>
          <GoogleReviewsWidget />
        </Container>
      </Section>

      {/* Cierre */}
      <Section sx={{ backgroundColor: theme.palette.secondary.dark, mb: "-20px", textAlign: "center" }}>
        <Container maxWidth="md">
          <Reveal>
            <SectionTitle overline="Tu turno" title="Viví tu propia historia en el campo" light align="center">
              Escribinos y te ayudamos a planear tu estadía en Cabañas del Campo.
            </SectionTitle>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                href={CONTACT_INFO.reservarLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Consultar disponibilidad
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/alojamiento"
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.6)",
                  "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.08)" },
                }}
              >
                Conocé las cabañas
              </Button>
            </Box>
          </Reveal>
        </Container>
      </Section>
    </Box>
  );
};

export default NuestrosHuspedes;
